"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { CloseIcon } from "./icons";
import { reportNavPanelOpen, type NavOpenReport } from "./nav-open-report";
import styles from "./global-nav.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * A nav item that opens a small panel of links — the APG disclosure pattern on
 * a native `<details>`, so it works before any JavaScript arrives and the
 * summary announces its own expanded state. This file adds Escape, outside-
 * click and scroll dismissal, closing on a link follow, and the close button.
 *
 * ⚠ Deliberately NOT a focus trap and NOT role="menu". The APG contract for a
 * disclosure is that Tab moves on and Escape closes; role="menu" brings a
 * keyboard contract these plain links do not honour.
 */

/**
 * Disclosures in one `<details name>` group close each other natively. ⚠ The
 * name is document-scoped, so the bar and drawer copies of an item — both
 * mounted at every width — need the `group` suffix to stay independent.
 */
const GROUP = "dds-global-nav-disclosure";

export interface NavDisclosureProps {
  /** Trigger content — the wording, or an icon for a bar control. */
  summary: React.ReactNode;
  /** Classes that make the summary look like the anchor it stands in for. */
  summaryClassName?: string;
  /** Reporting attributes for the trigger. The caller builds them because the
   *  placement depends on which copy of the item this is. Attributes only —
   *  a trigger that pushes through `openReport` takes none of them. */
  summaryTracking?: Record<string, string>;
  /** Reports the OPENING click and the panel's page view, from here. A trigger
   *  given this carries no `summaryTracking`, or the click reports twice. */
  openReport?: NavOpenReport;
  /** Positions the floating panel when the trigger sits in the link row. */
  panelClassName?: string;
  /** Suffixes the `<details name>` group — one group per rendered copy. */
  group?: string;
  children: React.ReactNode;
}

function NavDisclosureImpl({
  summary,
  summaryClassName,
  summaryTracking,
  openReport,
  panelClassName,
  group,
  children
}: NavDisclosureProps) {
  const [open, setOpen] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const summaryRef = useRef<HTMLElement>(null);

  /* Closing writes the DOM property rather than state: the element is the
     source of truth, and the write fires `toggle` where state catches up. */
  const close = useCallback((restoreFocus = true) => {
    const details = detailsRef.current;
    if (details) {
      details.open = false;
    }
    if (restoreFocus) {
      summaryRef.current?.focus({ preventScroll: true });
    }
  }, []);

  /* Any scroll closes it: the panel hangs off a trigger inside the bar, so a
     bar that hides on scroll carries the panel away and leaves it open where
     nobody can see it. Unconditional, because measuring whether the bar really
     left needs a `transitionend` that never fires under reduced motion. */
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    /* Focus returns to the trigger only when it was inside this disclosure; a
       reader who scrolled with focus elsewhere does not get it yanked away. */
    const onScroll = () => {
      close(detailsRef.current?.contains(document.activeElement) ?? false);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [open, close]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!detailsRef.current?.contains(event.target as Node)) {
        close(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, close]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape" && detailsRef.current?.open) {
      /* Innermost wins: inside the mobile drawer, Escape closes this panel and
         must not fall through to the drawer's own document-level handler. */
      event.stopPropagation();
      close();
    }
  };

  /* A link follow dismisses without touching focus — the browser is moving it
     to the destination. Delegated, because the links are server-rendered. */
  const onPanelClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("a[href]")) {
      close(false);
    }
  };

  /* Opening clicks only: the click lands before the browser toggles `details`,
     so one still closed here means this click opens it. Keyboard activation
     arrives as the same click. */
  const onSummaryClick = () => {
    if (openReport && !detailsRef.current?.open) {
      reportNavPanelOpen({
        ...openReport,
        pageView: true
      });
    }
  };

  /* A `<details name>` group only reaches its own group, so this cross-group
     rule keeps one panel open at a time for what the reader can SEE: opening
     one closes every other VISIBLE nav disclosure, hidden twins excepted.
     It is also the only half jsdom exercises — it has no `name` exclusivity. */
  const onToggle = (event: React.SyntheticEvent<HTMLDetailsElement>) => {
    const self = event.currentTarget;
    setOpen(self.open);
    if (self.open) {
      document
        .querySelectorAll<HTMLDetailsElement>(`details[name^="${GROUP}"][open]`)
        .forEach(other => {
          if (other !== self && other.offsetParent !== null) {
            other.open = false;
          }
        });
    }
  };

  return (
    <details
      className={styles.disclosure}
      name={group ? `${GROUP}-${group}` : GROUP}
      onKeyDown={onKeyDown}
      onToggle={onToggle}
      ref={detailsRef}
    >
      <summary
        /* Spread FIRST so this component's own props win. */
        {...summaryTracking}
        className={summaryClassName}
        onClick={onSummaryClick}
        ref={summaryRef}
      >
        {summary}
      </summary>
      <div className={`${styles.dropPanel} ${panelClassName ?? ""}`} onClick={onPanelClick}>
        <button
          aria-label="Close"
          className={styles.dropClose}
          onClick={() => close()}
          type="button"
        >
          <CloseIcon />
        </button>
        {children}
      </div>
    </details>
  );
}
export const NavDisclosure = withSafeRender(NavDisclosureImpl, "NavDisclosure");

export default NavDisclosure;
